import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { z } from "zod";
import { Box, CircularProgress, Typography } from "@mui/material";
import axiosInstance from "utils/request";
import {
  DomainSchema,
  ProjectSchema,
  ObjectTypeSchema,
  WorkflowStatusSchema,
  Domain,
  Project,
  ObjectType,
  WorkflowStatus,
} from "./schemas";

type AppData = {
  domains: Record<number, Domain>;
  projects: Record<number, Project>;
  objectTypes: Record<number, ObjectType>;
  workflowStatuses: Record<number, WorkflowStatus>;
};

type State =
  | { status: "loading" }
  | { status: "ready"; data: AppData }
  | { status: "error" };

const AppDataContext = createContext<AppData | null>(null);

export function useAppData(): AppData {
  const ctx = useContext(AppDataContext);
  if (!ctx) {
    throw new Error("useAppData must be used inside AppDataProvider");
  }
  return ctx;
}

export function AppDataProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>({ status: "loading" });

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        const [domainsRes, projectsRes, objectTypesRes, workflowStatusesRes] =
          await Promise.all([
            axiosInstance.get("/domains"),
            axiosInstance.get("/projects"),
            axiosInstance.get("/object_types"),
            axiosInstance.get("/workflow_statuses"),
          ]);
        const domainsList = z.array(DomainSchema).parse(domainsRes.data);
        const projectsList = z.array(ProjectSchema).parse(projectsRes.data);
        const objectTypesList = z
          .array(ObjectTypeSchema)
          .parse(objectTypesRes.data);
        const workflowStatusesList = z
          .array(WorkflowStatusSchema)
          .parse(workflowStatusesRes.data);
        const domains = Object.fromEntries(domainsList.map((d) => [d.id, d]));
        const projects = Object.fromEntries(projectsList.map((p) => [p.id, p]));
        const objectTypes = Object.fromEntries(
          objectTypesList.map((t) => [t.id, t]),
        );
        const workflowStatuses = Object.fromEntries(
          workflowStatusesList.map((s) => [s.id, s]),
        );
        if (mounted) {
          setState({
            status: "ready",
            data: { domains, projects, objectTypes, workflowStatuses },
          });
        }
      } catch {
        if (mounted) setState({ status: "error" });
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, []);

  if (state.status === "loading") {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
        data-testid="app-data-loading"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (state.status === "error") {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          gap: 2,
        }}
        data-testid="app-data-error"
      >
        <Typography variant="h5">Failed to load app data</Typography>
        <Typography variant="body2" color="text.secondary">
          Please refresh the page to try again.
        </Typography>
      </Box>
    );
  }

  return (
    <AppDataContext.Provider value={state.data}>
      {children}
    </AppDataContext.Provider>
  );
}
